import {
	Banner,
	Button,
	Checkbox,
	FormLayout,
	Heading,
	Icon,
	Modal,
	Stack,
	Tag,
	TextField,
	Tooltip,
} from "@shopify/polaris"
import { SettingsMajor } from "@shopify/polaris-icons"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFirestoreDataUpdate } from "../hooks/useFirestoreDataUpdate.js"
import { LanguageSelector } from "./LanguageSelector.jsx"
import PriceRounding from "./PriceRounding.jsx"
import VendorField from "./VendorField.jsx"
import { GlobalStateContext } from "./providers/GlobalStateProvider.jsx"
import { DefaultTranslateLanguageSelector } from "./DefaultTranslateLanguageSelector.jsx"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"
import { Spinner } from "./Spinner.jsx"
import { useSearchParams } from "react-router-dom"

const Settings = () => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const authenticatedFetch = useAuthenticatedFetch()
	const [searchParams, setSearchParams] = useSearchParams()

	const [open, setOpen] = useState(false)
	const [tagString, setTagString] = useState("")
	const [reapplyingKopyProductPages, setReapplyingKopyProductPages] =
		useState(false)

	const updateFirestoreUserData = useFirestoreDataUpdate()

	const addTag = (tag) => {
		const newTags = [...state.defaultTags, tag]
		dispatch({ defaultTags: newTags })
		setTagString("")
		updateFirestoreUserData({ defaultTags: newTags })
	}

	const removeTag = (tag) => {
		const newTags = state.defaultTags.filter((t) => t !== tag)
		dispatch({ defaultTags: newTags })
		updateFirestoreUserData({ defaultTags: newTags })
	}

	const togglePublishByDefault = (change) => {
		dispatch({ publishByDefault: change })
		updateFirestoreUserData({ publishByDefault: change })
	}

	const toggleStatusActiveByDefault = (change) => {
		dispatch({ statusActiveByDefault: change })
		updateFirestoreUserData({ statusActiveByDefault: change })
	}

	const toggleRoundingEnabledByDefault = (change) => {
		if (change === false) {
			dispatch({
				roundingEnabledByDefault: change,
				roundingNumberByDefault: null,
			})
			updateFirestoreUserData({
				roundingEnabledByDefault: change,
				roundingNumberByDefault: null,
			})
		} else {
			dispatch({ roundingEnabledByDefault: change })
			updateFirestoreUserData({ roundingEnabledByDefault: change })
		}
	}

	const changeRoundingNumberByDefault = (change) => {
		dispatch({ roundingNumberByDefault: change })
		updateFirestoreUserData({ roundingNumberByDefault: change })
	}

	const changeDefaultVendor = (change) => {
		dispatch({ defaultVendor: change })
		updateFirestoreUserData({ defaultVendor: change })
	}

	const toggleTaxableByDefault = (change) => {
		dispatch({ taxableByDefault: change })
		updateFirestoreUserData({ taxableByDefault: change })
	}

	const toggleSkipDuplicates = (change) => {
		dispatch({ skipDuplicates: change })
		updateFirestoreUserData({ skipDuplicates: change })
	}

	const toggleDisableCurrencyConversion = (change) => {
		dispatch({ disableCurrencyConversion: change })
		updateFirestoreUserData({ disableCurrencyConversion: change })
	}

	const reapplyKopyProductPages = async () => {
		setReapplyingKopyProductPages(true)
		await authenticatedFetch("/api/ai/reapplyProductPages", {
			method: "POST",
		}).catch((error) =>
			console.error("Error reapplying Kopy Product Pages", error),
		)
		setReapplyingKopyProductPages(false)
	}

	useEffect(() => {
		const settingsParam = searchParams.has("settings")

		if (settingsParam) {
			setOpen(true)
		}
	}, [])

	return (
		<Tooltip
			content={
				<div style={{ padding: "0.2rem 0.4rem" }}>
					<span style={{ fontSize: "0.8rem", color: "#222" }}>
						{t("header.settings_tooltip")}
					</span>
				</div>
			}
		>
			<div
				style={{
					background: "#e7e7e7",
					borderRadius: "100%",
					padding: "0.4rem",
				}}
			>
				<div
					onClick={() => setOpen(true)}
					style={{ cursor: "pointer", verticalAlign: "middle" }}
				>
					<Icon source={SettingsMajor} color="subdued" />
				</div>

				<Modal
					title={
						<div
							style={{
								display: "flex",
								gap: "0.5rem",
								alignItems: "center",
								color: "#333",
							}}
						>
							<svg
								height="1.2rem"
								width="1.2rem"
								viewBox="0 0 20 20"
								focusable="false"
								aria-hidden="true"
							>
								<path
									fill="currentColor"
									fillRule="evenodd"
									d="M9.027 0a1 1 0 0 0-.99.859l-.37 2.598a6.993 6.993 0 0 0-1.925 1.113l-2.437-.98a1 1 0 0 0-1.239.428l-1.132 1.963a1 1 0 0 0 .248 1.287l2.066 1.621a7.06 7.06 0 0 0 0 2.222l-2.066 1.621a1 1 0 0 0-.248 1.287l1.132 1.962a1 1 0 0 0 1.239.428l2.438-.979a6.995 6.995 0 0 0 1.923 1.113l.372 2.598a1 1 0 0 0 .99.859h2.265a1 1 0 0 0 .99-.859l.371-2.598a6.995 6.995 0 0 0 1.924-1.112l2.438.978a1 1 0 0 0 1.238-.428l1.133-1.962a1 1 0 0 0-.249-1.287l-2.065-1.621a7.063 7.063 0 0 0 0-2.222l2.065-1.621a1 1 0 0 0 .249-1.287l-1.133-1.962a1 1 0 0 0-1.239-.428l-2.437.979a6.994 6.994 0 0 0-1.924-1.113l-.371-2.597a1 1 0 0 0-.99-.859h-2.266zm1.133 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
								></path>
							</svg>
							<span>{t("settings.settings")}</span>
						</div>
					}
					open={open}
					onClose={() => setOpen(false)}
				>
					<Modal.Section>
						<Heading>{t("common.language")}</Heading>
						<div
							style={{
								marginTop: "1rem",
								display: "flex",
								justifyContent: "start",
							}}
						>
							<LanguageSelector />
						</div>
					</Modal.Section>

					{state.hasValidKopyPlusSubscription && (
						<Modal.Section>
							<Heading>
								{t("settings.default_ai_translation_language")}
							</Heading>
							<div
								style={{
									marginTop: "1rem",
									display: "flex",
									justifyContent: "start",
								}}
							>
								<DefaultTranslateLanguageSelector />
							</div>
						</Modal.Section>
					)}

					{state.hasValidKopyPlusSubscription && (
						<Modal.Section>
							<Heading>
								{t("ai.re-apply_kopy_product_pages")}
							</Heading>
							<p style={{ fontSize: "0.85rem" }}>
								{t(
									"ai.if_you_have_recently_changed_your_store_theme",
								)}
							</p>
							<div
								style={{
									marginTop: "1rem",
									display: "flex",
									justifyContent: "start",
								}}
							>
								<Button
									disabled={reapplyingKopyProductPages}
									onClick={reapplyKopyProductPages}
								>
									{reapplyingKopyProductPages ? (
										<Spinner />
									) : (
										t("ai.re-apply_kopy_product_pages")
									)}
								</Button>
							</div>
							{reapplyingKopyProductPages && (
								<div style={{ marginTop: "1rem" }}>
									<Banner status="info">
										{t(
											"ai.your_kopy_product_pages_are_now_being_re-applied",
										)}
									</Banner>
								</div>
							)}
						</Modal.Section>
					)}

					<Modal.Section>
						<Heading>
							{t("settings.tags_to_be_added_to_all_products")}
						</Heading>
						<p style={{ marginTop: "1rem" }}>
							{t(
								"settings.add_multiple_by_separating_with_a_comma",
							)}
						</p>
						<FormLayout>
							<TextField
								value={tagString}
								onChange={(change) => setTagString(change)}
								connectedRight={
									<Button onClick={() => addTag(tagString)}>
										{t("common.add")}
									</Button>
								}
							/>
							<Stack spacing="extraTight">
								{state.defaultTags.map((tag) => (
									<Tag
										key={tag}
										onRemove={() => removeTag(tag)}
									>
										{tag}
									</Tag>
								))}
							</Stack>
						</FormLayout>
					</Modal.Section>

					<Modal.Section>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>
								{t("settings.active_status_by_default")}
							</Heading>
						</div>
						<Checkbox
							label={t(
								"settings.set_product_status_to_active_by_default",
							)}
							checked={state.statusActiveByDefault}
							onChange={() =>
								toggleStatusActiveByDefault(
									!state.statusActiveByDefault,
								)
							}
						/>
					</Modal.Section>

					<Modal.Section>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>
								{t("settings.publish_by_default")}
							</Heading>
						</div>
						<Checkbox
							label={t(
								"settings.publish_product_to_store_by_default",
							)}
							checked={state.publishByDefault}
							onChange={() =>
								togglePublishByDefault(!state.publishByDefault)
							}
						/>
					</Modal.Section>

					<Modal.Section>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>
								{t("settings.default_price_rounding")}
							</Heading>
						</div>
						<PriceRounding
							roundingEnabled={state.roundingEnabledByDefault}
							onCheckedChanged={(change) =>
								toggleRoundingEnabledByDefault(change)
							}
							roundingNumber={state.roundingNumberByDefault}
							onRoundingNumberChange={(change) =>
								changeRoundingNumberByDefault(change)
							}
						/>
					</Modal.Section>

					<Modal.Section>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>
								{t("settings.default_product_vendor")}
							</Heading>
						</div>
						<VendorField
							vendor={state.defaultVendor}
							onVendorChange={(change) =>
								changeDefaultVendor(change)
							}
						/>
					</Modal.Section>

					<Modal.Section>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>
								{t("settings.taxable_by_default")}
							</Heading>
						</div>
						<Checkbox
							label={t(
								"settings.make_products_imported_taxable_by_default",
							)}
							checked={state.taxableByDefault}
							onChange={() =>
								toggleTaxableByDefault(!state.taxableByDefault)
							}
						/>
					</Modal.Section>

					<Modal.Section>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>{t("settings.skip_duplicates")}</Heading>
						</div>
						<Checkbox
							label={t(
								"settings.skip_products_already_found_in_your_store_by_product_handle",
							)}
							checked={state.skipDuplicates}
							onChange={() =>
								toggleSkipDuplicates(!state.skipDuplicates)
							}
						/>
					</Modal.Section>

					<Modal.Section>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>
								{t("settings.disable_currency_conversion")}
							</Heading>
						</div>
						<Checkbox
							label={t(
								"settings.disable_the_conversion_of_currencies_when_importing",
							)}
							checked={state.disableCurrencyConversion}
							onChange={() =>
								toggleDisableCurrencyConversion(
									!state.disableCurrencyConversion,
								)
							}
						/>
					</Modal.Section>
				</Modal>
			</div>
		</Tooltip>
	)
}

export default Settings
